import { useMemo } from 'react';
import { getAgGridColId } from './columns/getAgGridColId';
import type { Column } from './columns/types';
import { COLUMN_GROUP_NATIVE_TYPE, type ColumnGroup, type ColumnOrColumnGroup } from './types';
import { useDefaultColumns } from './useDefaultColumns';

export type ColumnGroupDefinition = Omit<ColumnGroup, 'children' | 'type'> & {
  columnIds: readonly string[];
};

export type UseDefaultColumnsWithGroupsOptions = {
  /** Column ids or overrides to resolve through useDefaultColumns. */
  defaultColumns: (string | Partial<Column>)[];
  /** Full column definition map for the blotter. */
  columnDefinitions: Map<string, Column>;
  /** Group definitions whose columnIds are resolved into children. */
  columnGroups: readonly ColumnGroupDefinition[];
  /** Column ids to append outside native groups. */
  ungroupedColumnIds?: readonly string[];
  /** When false, skip the grouped-header class on child columns. */
  markGroupedColumnHeaders?: boolean;
};

const EMPTY_COLUMN_IDS: readonly string[] = [];

/**
 * Resolves default column ids into column definitions, then wraps configured ids
 * in native column groups and appends explicit ungrouped column ids.
 *
 * @returns Column groups plus explicitly ungrouped columns for useBlotterTable.
 */
export function useDefaultColumnsWithGroups({
  defaultColumns,
  columnDefinitions,
  columnGroups,
  ungroupedColumnIds = EMPTY_COLUMN_IDS,
  markGroupedColumnHeaders = true,
}: UseDefaultColumnsWithGroupsOptions): ColumnOrColumnGroup[] {
  const columns = useDefaultColumns(defaultColumns, columnDefinitions);

  return useMemo<ColumnOrColumnGroup[]>(() => {
    // Index resolved columns once so group and ungrouped ids can share the same lookup.
    const columnsById = new Map(columns.map(column => [getAgGridColId(column), column]));

    // Resolve configured group/ungrouped ids to column definitions; skip ids missing from the definition map.
    const getColumns = (columnIds: readonly string[]): Column[] =>
      columnIds.flatMap(columnId => {
        const column = columnsById.get(columnId);
        return column ? [column] : [];
      });

    return [
      ...columnGroups.map(
        ({ columnIds, ...group }): ColumnGroup => ({
          ...group,
          type: COLUMN_GROUP_NATIVE_TYPE,
          children: getColumns(columnIds).map(column =>
            markGroupedColumnHeaders ? { ...column, columnGroup: true } : column
          ),
        })
      ),
      ...getColumns(ungroupedColumnIds),
    ];
  }, [columnGroups, columns, markGroupedColumnHeaders, ungroupedColumnIds]);
}
